import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { askReason } from '../utils/reportError';

// Finance · Bill-wise allocation — the screen behind mig 344.
//
// ── TWO MODES, AND THEY LOOK DIFFERENT ON PURPOSE ───────────────────────────────────────────
// The measurement says these are two jobs, not one list of two lengths:
//
//   FAST   RSR — 31 payees, 45 cheques, Rs 52 lakh. A tedious list where keying speed matters.
//          Compact rows, bills inline, the amount pre-filled, one keystroke per bill.
//   CAREFUL Deroi — 6 payees, Rs 81 lakh. Few, large, each worth care.
//          One cheque at a time, full width, big figures, bills as cards, a confirm step.
//
// The layouts differ rather than the filter, and that is deliberate: somebody who has spent the
// morning clicking through 45 small RSR cheques will carry that rhythm into a Rs 20 lakh allocation
// if the second screen is the first one with fewer rows. The hands have to notice.
//
// ── WHAT THE SCREEN GUARDS THAT THE SCHEMA CANNOT ───────────────────────────────────────────
// purchase_invoices.supplier is FREE TEXT while pdc_cheques.payee_id is an FK (mig 344's named gap),
// so the bills offered against a payee are matched on NORMALISED NAME and nothing in the database
// objects if a differently-spelled party's bill is allocated. THE RAW SUPPLIER STRING IS SHOWN ON
// EVERY BILL ROW, uncleaned. The spelling is the signal.

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const inr = (v) => '₹' + num(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const inr0 = (v) => '₹' + num(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const input = { padding: '6px 8px', border: '1px solid #ddd', borderRadius: 5, fontSize: 12, boxSizing: 'border-box' };
const btn = (bg) => ({ padding: '6px 12px', border: 'none', borderRadius: 5, background: bg, color: '#fff',
                       fontSize: 12, fontWeight: 600, cursor: 'pointer' });

// The five states rendered AS STATES. `unallocated` is never blank and never a dash — the whole
// point of mig 344's on-account declaration is that "nobody has said" is a fact with a name.
const STATE = {
  unallocated:                     { label: 'nobody has said yet', bg: '#fff4e5', fg: '#8a5a00' },
  part_allocated:                  { label: 'part allocated',      bg: '#e8f1fb', fg: '#1b4f8a' },
  part_allocated_rest_on_account:  { label: 'part allocated · rest on account', bg: '#eef3e8', fg: '#3c5a1e' },
  on_account:                      { label: 'ON ACCOUNT (advance)', bg: '#efe8f7', fg: '#5a2d82' },
  allocated:                       { label: 'allocated',           bg: '#e6f4ea', fg: '#1e6b33' },
};
const Pill = ({ s }) => {
  const c = STATE[s] || { label: s, bg: '#eee', fg: '#555' };
  return <span style={{ background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 10,
                        fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{c.label}</span>;
};

export default function BillAllocation() {
  const [mode, setMode] = useState('fast');          // 'fast' | 'careful'
  const [showResolved, setShowResolved] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(null);            // cheque_id whose bills are expanded
  const [bills, setBills] = useState({ data: [], near_spelling: [], cheque: null });
  const [allocs, setAllocs] = useState([]);
  const [amounts, setAmounts] = useState({});        // invoice_id -> typed amount
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [oaReason, setOaReason] = useState('');
  // TWO PEOPLE (mig 376): an allocation is PROPOSED by one named person and CONFIRMED by another.
  const [proposals, setProposals] = useState([]);
  const loadProposals = () => api.get('/finance/allocation/proposals')
    .then(r => setProposals(r.data.data || []))
    .catch(e => setMsg({ bad: true, t: e?.response?.data?.error || 'Could not load allocations awaiting a second person.' }));
  useEffect(() => { loadProposals(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    api.get(`/finance/allocation/cheques?resolved=${showResolved ? 1 : 0}`)
      .then(r => { setRows(r.data.data || []); setSummary(r.data.summary || null); })
      .catch(e => setMsg({ bad: true, t: e?.response?.data?.error || 'Could not load cheques.' }));
  }, [showResolved]);

  useEffect(() => { load(); }, [load]);

  const openCheque = (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id); setAmounts({}); setOaReason(''); setMsg(null);
    api.get(`/finance/allocation/cheques/${id}/bills`)
      .then(r => {
        setBills(r.data);
        // THE DEFAULT IS min(bill outstanding, cheque remainder) — computed server-side as
        // default_amount so the screen and the schema agree on it. Always valid, never refused on
        // the first click, and equal to the full outstanding in the common case.
        const seed = {};
        for (const b of (r.data.data || [])) seed[b.id] = String(num(b.default_amount).toFixed(2));
        setAmounts(seed);
      })
      .catch(e => setMsg({ bad: true, t: e?.response?.data?.error || 'Could not load bills.' }));
    api.get(`/finance/allocation/cheques/${id}/allocations`)
      .then(r => setAllocs(r.data.data || [])).catch(() => setAllocs([]));
  };

  const allocate = async (chequeId, lines) => {
    if (!lines.length) { setMsg({ bad: true, t: 'Nothing selected.' }); return; }
    setBusy(true); setMsg(null);
    try {
      await api.post(`/finance/allocation/cheques/${chequeId}/allocate`, { lines });
      setMsg({ t: 'Proposed. A second named person must confirm which bills this cheque pays — nothing is recorded until they do.' });
      setAmounts({}); setOpen(null); load(); loadProposals();
    } catch (e) {
      setMsg({ bad: true, t: e?.response?.data?.error || 'Proposal refused.' });
    } finally { setBusy(false); }
  };

  const declareOnAccount = async (chequeId) => {
    if (!oaReason.trim()) { setMsg({ bad: true, t: 'An on-account declaration needs a reason.' }); return; }
    setBusy(true);
    try {
      await api.post(`/finance/allocation/cheques/${chequeId}/on-account`, { reason: oaReason.trim() });
      setMsg({ t: 'Declared on account. It leaves the list.' }); setOpen(null); setOaReason(''); load();
    } catch (e) { setMsg({ bad: true, t: e?.response?.data?.error || 'Refused.' }); }
    finally { setBusy(false); }
  };

  const reverse = async (paymentId) => {
    const reason = askReason('Why is this allocation being reversed?');
    if (reason == null) return;
    setBusy(true);
    try {
      await api.post(`/finance/allocation/${paymentId}/reverse`, { reason: reason.trim() });
      setMsg({ t: 'Reversed. The bill and the cheque both show the money again.' });
      load(); if (open) openCheque(open);
    } catch (e) { setMsg({ bad: true, t: e?.response?.data?.error || 'Refused.' }); }
    finally { setBusy(false); }
  };

  const decide = async (p, act) => {
    let body = {};
    if (act === 'reject') { const reason = askReason(`What is wrong with ${p.proposed_by_name}'s allocation of cheque ${p.cheque_no}?`); if (reason == null) return; body = { reason: reason.trim() }; }
    setBusy(true); setMsg(null);
    try {
      await api.post(`/finance/allocation/proposals/${p.id}/${act}`, body);
      setMsg({ t: act === 'confirm' ? `Confirmed. Cheque ${p.cheque_no} is recorded against ${p.lines.length} bill(s).`
        : act === 'reject' ? 'Rejected — the proposer can propose it again.' : 'Withdrawn.' });
      load(); loadProposals();
    } catch (e) { setMsg({ bad: true, t: e?.response?.data?.error || 'Refused.' }); }
    finally { setBusy(false); }
  };

  const linesFrom = (list) => list
    .filter(b => num(amounts[b.id]) > 0)
    .map(b => ({ invoice_id: b.id, amount: num(amounts[b.id]) }));

  // ── THE SUPPLIER STRING, SHOWN RAW ─────────────────────────────────────────────────────────
  const SupplierRaw = ({ b }) => (
    <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11,
                   color: b.spelling_differs ? '#a33' : '#555' }}>
      {b.supplier_raw || '(blank)'}
      {b.spelling_differs && <span title="This invoice's supplier text does not match the payee's canonical name. Check it is the same party — nothing in the database will."
        style={{ marginLeft: 5, fontWeight: 700 }}>⚠</span>}
    </span>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Bill-wise allocation</h3>
        {/* The mode switch is a switch, not a filter — the layouts below are different shapes. */}
        <div style={{ display: 'flex', border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
          <button onClick={() => { setMode('fast'); setOpen(null); }}
            style={{ ...btn(mode === 'fast' ? '#1b4f8a' : '#f4f4f4'), borderRadius: 0,
                     color: mode === 'fast' ? '#fff' : '#555' }}>⚡ Fast list</button>
          <button onClick={() => { setMode('careful'); setOpen(null); }}
            style={{ ...btn(mode === 'careful' ? '#5a2d82' : '#f4f4f4'), borderRadius: 0,
                     color: mode === 'careful' ? '#fff' : '#555' }}>🔍 One at a time</button>
        </div>
        <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={showResolved} onChange={e => { setShowResolved(e.target.checked); setOpen(null); }} />
          show resolved too
        </label>
        {summary && (
          <span style={{ fontSize: 12, color: '#555', marginLeft: 'auto' }}>
            <b>{summary.unresolved}</b> unresolved of {summary.total} · <b>{inr0(summary.unresolved_value)}</b> unallocated
          </span>
        )}
      </div>

      {msg && <div style={{ padding: '8px 11px', borderRadius: 6, marginBottom: 10, fontSize: 12,
                            background: msg.bad ? '#fdecea' : '#e6f4ea', color: msg.bad ? '#a33' : '#1e6b33' }}>{msg.t}</div>}

      {proposals.length > 0 && (
        <div style={{ marginBottom: 14, border: '1px solid #e2d6f0', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: '#f6f1fb', fontSize: 12, fontWeight: 700, color: '#5a2d82' }}>
            Waiting for a second person · {proposals.length} allocation{proposals.length === 1 ? '' : 's'} — check WHICH bills each cheque pays
          </div>
          {proposals.map(p => (
            <div key={p.id} style={{ padding: '9px 12px', borderTop: '1px solid #f0eaf6', display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', fontSize: 12 }}>
              <div style={{ flex: '1 1 320px' }}>
                <b>Cheque {p.cheque_no}</b> · {p.party_name} · {inr(p.cheque_amount)} · cleared {fmtD(p.cleared_date)}
                <div style={{ color: '#777', marginTop: 2 }}>proposed by {p.proposed_by_name} · {fmtD(p.proposed_at)}{p.note ? ` — ${p.note}` : ''}</div>
                <ul style={{ margin: '5px 0 0 16px', padding: 0 }}>
                  {p.lines.map(l => <li key={l.invoice_id}>{l.invoice_no} · <span style={{ fontFamily: 'ui-monospace, monospace', color: '#555' }}>{l.supplier}</span> · <b>{inr(l.amount)}</b></li>)}
                </ul>
              </div>
              {p.mine ? (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#777', fontSize: 11, marginBottom: 4 }}>Yours — someone else confirms it.</div>
                  <button disabled={busy} onClick={() => decide(p, 'withdraw')} style={{ ...btn('#888'), padding: '4px 10px', fontSize: 11 }}>Withdraw</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button disabled={busy} onClick={() => decide(p, 'reject')} style={{ ...btn('#b3402f'), padding: '5px 10px', fontSize: 11 }}>Reject</button>
                  <button disabled={busy} onClick={() => decide(p, 'confirm')} style={{ ...btn('#1e6b33'), padding: '5px 12px', fontSize: 11 }}>Confirm {inr(p.total)}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!rows.length && (
        <div style={{ padding: 24, textAlign: 'center', color: '#777', fontSize: 13, background: '#fafafa', borderRadius: 8 }}>
          {showResolved ? 'No cheques on this book.'
            : 'Nothing left to allocate on this book. Every cheque is either fully allocated or declared on account.'}
        </div>
      )}

      {/* ══ FAST MODE ══ compact table, bills inline, built for keying ══════════════════════ */}
      {mode === 'fast' && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f4f6f8', textAlign: 'left' }}>
                <th style={{ padding: '7px 8px' }}>Cheque</th>
                <th style={{ padding: '7px 8px' }}>Payee</th>
                <th style={{ padding: '7px 8px', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '7px 8px', textAlign: 'right' }}>Unallocated</th>
                <th style={{ padding: '7px 8px' }}>State</th>
                <th style={{ padding: '7px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <React.Fragment key={c.cheque_id}>
                  <tr style={{ borderTop: '1px solid #eee', background: open === c.cheque_id ? '#f8fbff' : undefined }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'ui-monospace, monospace' }}>
                      {c.cheque_no}<div style={{ color: '#888', fontSize: 10 }}>{fmtD(c.due_date)} · {c.status}</div>
                    </td>
                    <td style={{ padding: '6px 8px' }}>{c.party_name}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(c.amount)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                 fontWeight: 700, color: num(c.unallocated) > 0.01 ? '#8a5a00' : '#1e6b33' }}>
                      {inr(c.unallocated)}
                    </td>
                    <td style={{ padding: '6px 8px' }}><Pill s={c.allocation_state} /></td>
                    <td style={{ padding: '6px 8px' }}>
                      <button onClick={() => openCheque(c.cheque_id)} style={btn('#1b4f8a')}>
                        {open === c.cheque_id ? 'Close' : 'Allocate'}
                      </button>
                    </td>
                  </tr>
                  {open === c.cheque_id && (
                    <tr><td colSpan={6} style={{ padding: 0, background: '#f8fbff' }}>
                      <div style={{ padding: '10px 12px' }}>
                        {!bills.data.length && <div style={{ color: '#a33', fontSize: 12 }}>
                          No open bills for this payee. Either the bills have not been entered yet — which is
                          the larger half of this job — or they are filed under a different supplier spelling.
                        </div>}
                        {bills.data.length > 0 && (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead><tr style={{ color: '#666', textAlign: 'left' }}>
                              <th style={{ padding: 4 }}>Bill</th>
                              <th style={{ padding: 4 }}>Supplier, as written on the invoice</th>
                              <th style={{ padding: 4, textAlign: 'right' }}>Outstanding</th>
                              <th style={{ padding: 4, textAlign: 'right' }}>Allocate</th>
                            </tr></thead>
                            <tbody>
                              {bills.data.map(b => (
                                <tr key={b.id} style={{ borderTop: '1px solid #eef' }}>
                                  <td style={{ padding: 4 }}>{b.invoice_no}<span style={{ color: '#999' }}> · {fmtD(b.invoice_date)}</span></td>
                                  <td style={{ padding: 4 }}><SupplierRaw b={b} /></td>
                                  <td style={{ padding: 4, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(b.outstanding)}</td>
                                  <td style={{ padding: 4, textAlign: 'right' }}>
                                    <input style={{ ...input, width: 110, textAlign: 'right' }} value={amounts[b.id] ?? ''}
                                      onChange={e => setAmounts(a => ({ ...a, [b.id]: e.target.value }))} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 9, alignItems: 'center', flexWrap: 'wrap' }}>
                          <button disabled={busy} onClick={() => allocate(c.cheque_id, linesFrom(bills.data))}
                            style={btn('#1e6b33')} title="A second named person confirms before anything is recorded">Propose</button>
                          <span style={{ color: '#888', fontSize: 11 }}>or</span>
                          <input style={{ ...input, width: 240 }} placeholder="reason this is an advance…"
                            value={oaReason} onChange={e => setOaReason(e.target.value)} />
                          <button disabled={busy || !oaReason.trim()} onClick={() => declareOnAccount(c.cheque_id)}
                            style={btn(oaReason.trim() ? '#5a2d82' : '#bbb')}>Declare on account</button>
                        </div>
                        {allocs.length > 0 && <Existing allocs={allocs} onReverse={reverse} busy={busy} />}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ══ CAREFUL MODE ══ one cheque, full width, large figures, bills as cards ═══════════ */}
      {mode === 'careful' && rows.length > 0 && (
        <div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {rows.map(c => (
              <button key={c.cheque_id} onClick={() => openCheque(c.cheque_id)}
                style={{ padding: '10px 14px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                         border: open === c.cheque_id ? '2px solid #5a2d82' : '1px solid #ddd',
                         background: open === c.cheque_id ? '#faf7fd' : '#fff', minWidth: 190 }}>
                <div style={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{inr0(c.unallocated)}</div>
                <div style={{ fontSize: 12, color: '#444', marginTop: 2 }}>{c.party_name}</div>
                <div style={{ fontSize: 10, color: '#888', fontFamily: 'ui-monospace, monospace' }}>
                  {c.cheque_no} · {fmtD(c.due_date)}
                </div>
              </button>
            ))}
          </div>

          {open && bills.cheque && (
            <div style={{ border: '2px solid #5a2d82', borderRadius: 10, padding: 18, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 13, color: '#666' }}>{bills.cheque.party_name}</div>
                  <div style={{ fontSize: 30, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                    {inr(bills.cheque.remainder)}
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    unallocated of {inr(bills.cheque.amount)}
                  </div>
                </div>
                <button onClick={() => setOpen(null)} style={btn('#888')}>Close</button>
              </div>

              <div style={{ marginTop: 16 }}>
                {!bills.data.length && <div style={{ color: '#a33', fontSize: 13 }}>
                  No open bills for this payee. On Deroi's book that is the expected case — it has no
                  purchase invoices at all, so the bills have to be entered before this cheque can be
                  allocated to anything.
                </div>}
                {bills.data.map(b => (
                  <div key={b.id} style={{ border: '1px solid #e6e0ee', borderRadius: 8, padding: 13, marginBottom: 9,
                                           display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 260px' }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{b.invoice_no}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{fmtD(b.invoice_date)}</div>
                      <div style={{ marginTop: 4 }}><SupplierRaw b={b} /></div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#888' }}>outstanding</div>
                      <div style={{ fontSize: 17, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{inr(b.outstanding)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#888' }}>allocate</div>
                      <input style={{ ...input, width: 150, textAlign: 'right', fontSize: 15, padding: '8px 10px' }}
                        value={amounts[b.id] ?? ''} onChange={e => setAmounts(a => ({ ...a, [b.id]: e.target.value }))} />
                    </div>
                  </div>
                ))}
              </div>

              {/* A confirm step the fast list does not have. The figures here are large enough that
                  one extra deliberate act is cheaper than one reversal. */}
              <ConfirmBar busy={busy} lines={linesFrom(bills.data)} bills={bills}
                onAllocate={() => allocate(open, linesFrom(bills.data))} />

              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #eee', display: 'flex',
                            gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#666' }}>Not against a bill?</span>
                <input style={{ ...input, width: 300 }} placeholder="why this is an advance…"
                  value={oaReason} onChange={e => setOaReason(e.target.value)} />
                <button disabled={busy || !oaReason.trim()} onClick={() => declareOnAccount(open)}
                  style={btn(oaReason.trim() ? '#5a2d82' : '#bbb')}>Declare on account</button>
              </div>

              {bills.near_spelling.length > 0 && (
                <div style={{ marginTop: 14, padding: 11, background: '#fff8e5', borderRadius: 7, fontSize: 12 }}>
                  <b>{bills.near_spelling.length} open bill(s) under a DIFFERENT spelling of this name.</b> Listed
                  separately and not offered above, because the database cannot tell whether they are the same
                  party — `purchase_invoices.supplier` is free text. Read the spelling and decide:
                  <ul style={{ margin: '6px 0 0 18px' }}>
                    {bills.near_spelling.map(b => (
                      <li key={b.id} style={{ marginBottom: 2 }}>
                        <SupplierRaw b={{ ...b, spelling_differs: true }} /> — {b.invoice_no}, {inr(b.outstanding)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {allocs.length > 0 && <Existing allocs={allocs} onReverse={reverse} busy={busy} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The running total against the cheque, shown before the button rather than after the refusal.
function ConfirmBar({ lines, bills, onAllocate, busy }) {
  const total = lines.reduce((s, l) => s + num(l.amount), 0);
  const left = num(bills.cheque?.remainder) - total;
  const over = left < -0.01;
  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: over ? '#fdecea' : '#f4f8f4',
                  display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ fontSize: 13 }}>
        Proposing <b style={{ fontVariantNumeric: 'tabular-nums' }}>{inr(total)}</b> across {lines.length} bill(s) — a second person confirms before it is recorded.
        {over
          ? <span style={{ color: '#a33', fontWeight: 600 }}> That is {inr(-left)} more than the cheque has left.</span>
          : <span style={{ color: '#555' }}> {inr(left)} would remain unallocated{left > 0.01 ? ' — the cheque stays on the list.' : '.'}</span>}
      </div>
      <button disabled={busy || over || !lines.length} onClick={onAllocate}
        style={{ ...btn(over || !lines.length ? '#bbb' : '#1e6b33'), marginLeft: 'auto', padding: '9px 20px', fontSize: 13 }}>
        Propose allocation
      </button>
    </div>
  );
}

function Existing({ allocs, onReverse, busy }) {
  return (
    <div style={{ marginTop: 13, paddingTop: 11, borderTop: '1px dashed #ddd' }}>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 5, fontWeight: 600 }}>ALREADY ALLOCATED</div>
      {allocs.map(a => (
        <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 11,
                                 padding: '4px 0', opacity: a.reversed ? 0.55 : 1 }}>
          <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 96, textAlign: 'right',
                         textDecoration: a.reversed ? 'line-through' : 'none' }}>{inr(a.amount)}</span>
          <span>{a.invoice_no}</span>
          <span style={{ fontFamily: 'ui-monospace, monospace', color: '#777' }}>{a.supplier_raw}</span>
          <span style={{ color: '#999' }}>{a.recorded_by_name || '—'}</span>
          {a.reversed
            ? <span style={{ color: '#a33' }}>reversed — {a.reversed_reason}</span>
            : <button disabled={busy} onClick={() => onReverse(a.id)}
                style={{ ...btn('#b3402f'), padding: '3px 9px', fontSize: 10 }}>Reverse</button>}
        </div>
      ))}
    </div>
  );
}
